import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { emitter, emitOpenLoginDialog } from '@/plugins/emitter'
import { useAuth } from '@/composables/useAuth'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import { useAiStreaming } from '@/composables/yagiprime/useAiStreaming'
import { useAiMyprDialogControls } from '@/composables/format-edit/useAiMyprDialogControls'
import { createReasonAiMyprExtras } from '@/composables/yagiprime/useReasonAiMyprExtras'
import { createReasonAiMyprGenerate } from '@/composables/yagiprime/useReasonAiMyprGenerate'
import hopeIndustryOptionsJson from '@/assets/yagiprime/hope-industry-options.json'
import hopeJobcategoryOptionsJson from '@/assets/yagiprime/hope-jobcategory-options.json'

type FormatData = Record<string, unknown> & {
  reason?: string
  lastestEdu?: string
  lastestJob?: string
  ai_strength?: string
  ai_company_url?: string
  ai_company_name?: string
  ai_company_values?: string
  hopeIndustrycategory?: string
  hopeJobcategory?: string
  currentIndustrycategory?: string
  currentJobcategory?: string
  licenseHistories?: Array<{ description?: string }>
}

type AxiosVm = {
  $axios: {
    post: (
      url: string,
      data: unknown,
      config?: { timeout?: number }
    ) => Promise<{ data: Record<string, unknown> }>
    isCancel?: (err: unknown) => boolean
  }
}

export type UseReasonAiMyprOptions = {
  formatData: FormatData
  emit: {
    (e: 'update:reason', value: string): void
    (e: 'update-format-field', payload: { key: string; value: unknown }): void
  }
  updateFormatField: (key: string, value: unknown) => void
}

export function useReasonAiMypr(options: UseReasonAiMyprOptions) {
  const { formatData, emit, updateFormatField } = options
  const { user, isAuthenticated } = useAuth()
  const { vm, message, varuna, globalVar, yagiprimePaymentService } =
    useHostServices()
  const axios = (vm as unknown as AxiosVm).$axios
  const { aiMyprVisible, openAiMyprDialog, closeAiMyprDialog } =
    useAiMyprDialogControls()

  const aiResultView = ref(false)
  const aiResultMyprResult = ref('')
  const fullscreenLoading = ref(false)
  const loadingText = ref('AIがあなたに最適なテキストを生成しています')
  const aiGeneratedExampleReason = ref('')
  const aiGeneratedExampleReasonFull = ref('')
  const hopeIndustryOptions = hopeIndustryOptionsJson.options as string[]
  const hopeCareerJobcategoryOptions =
    hopeJobcategoryOptionsJson.options as string[]
  const isSendMail = ref(false)
  const correctSentence = ref('')
  const aiUsageCount = ref(0)
  const aiMaxUsageCount = ref(1)
  const isAutoExecuteAfterPayment = ref(false)
  const isExtractingCompanyInfo = ref(false)
  const isCompanyInfoExtracted = ref(false)
  const exampleDialogVisible = ref(false)
  const tipsDialogVisible = ref(false)
  const summarizeModalVisible = ref(false)

  const summarizePreview = reactive({
    visible: false,
    loading: false,
    errorMessage: '',
    rateLimited: false,
    summaries: [] as Array<{ text: string; length: number }>,
    abortController: null as AbortController | null,
    applied: false
  })

  const {
    aiIsStreaming,
    aiIsThinking,
    aiThinkingLabel,
    aiReasoningText,
    aiShowReasoning,
    startAIStream,
    toggleAiReasoning
  } = useAiStreaming({
    aiResultMyprResult,
    aiResultView,
    fullscreenLoading
  })

  const reason = computed({
    get: () => (formatData.reason as string) || '',
    set: (value: string) => emit('update:reason', value)
  })

  const getLicences = computed(() => {
    let licensesStr = ''
    const histories = formatData.licenseHistories || []
    for (const key in histories) {
      licensesStr +=
        (histories[key as unknown as number]?.description || '') + ','
    }
    return licensesStr
  })

  const aiMyprCtaState = computed(() => {
    if (globalVar.isYagiPrime) return 'available'
    return aiUsageCount.value >= 1 ? 'locked' : 'free'
  })

  const summarizeTargetLength = computed(() => {
    const current = (reason.value || '').length
    if (current <= 350) return Math.max(30, Math.floor(current * 0.85))
    return 300
  })

  const summarizePreviewFirst = computed(
    () => summarizePreview.summaries[0] || null
  )

  const summarizePreviewDisplayText = computed(() => {
    const first = summarizePreviewFirst.value
    if (!first || !first.text) return ''
    if (globalVar.isYagiPrime) return first.text
    const cutAt = Math.max(60, Math.floor(first.text.length * 0.45))
    return first.text.substring(0, cutAt) + '…'
  })

  function openLoginDialog() {
    emitOpenLoginDialog()
  }

  function openAiMyprModal() {
    if (window.innerWidth > 768) return
    openAiMyprDialog()
  }

  function reasonClear() {
    emit('update:reason', '')
    message({ message: '削除完了しました。', type: 'success' })
  }

  function updateReason(text: string) {
    emit('update:reason', text)
  }

  function getAiUsageCount() {
    if (!user.value) return
    const body = new FormData()
    body.append('uuid', user.value.uid)
    body.append('category_type', 'reason')
    axios.post(varuna.endPoints.getAiUsageCount, body).then((response) => {
      const data = response.data as { success?: boolean; usage_count?: number }
      if (data.success) {
        aiUsageCount.value = data.usage_count || 0
        aiMaxUsageCount.value = globalVar.isYagiPrime ? 10 : 1
      }
    })
  }

  function showModalFromNewAi() {
    if (isAuthenticated.value) getAiUsageCount()
    else aiUsageCount.value = 1
    openAiMyprModal()
  }

  const generate = createReasonAiMyprGenerate({
    formatData,
    isAuthenticated,
    user,
    axios,
    varuna,
    globalVar,
    message,
    vm,
    yagiprimePaymentService,
    aiResultView,
    aiResultMyprResult,
    aiIsStreaming,
    aiUsageCount,
    isAutoExecuteAfterPayment,
    isExtractingCompanyInfo,
    isCompanyInfoExtracted,
    aiMyprVisible,
    startAIStream,
    openAiMyprModal,
    closeAiMyprDialog,
    updateFormatField,
    updateReason,
    getAiUsageCount
  })

  const extras = createReasonAiMyprExtras({
    reason,
    summarizePreview,
    summarizeTargetLength,
    summarizePreviewFirst,
    summarizeModalVisible,
    isSendMail,
    correctSentence,
    aiGeneratedExampleReason,
    aiGeneratedExampleReasonFull,
    isAuthenticated,
    user,
    globalVar,
    axios,
    varuna,
    message,
    emit: (e, value) => emit(e, value),
    getAiUsageCount,
    vm
  })

  onMounted(() => {
    if (!('currentIndustrycategory' in formatData)) {
      updateFormatField('currentIndustrycategory', '')
    }
    if (!('currentJobcategory' in formatData)) {
      updateFormatField('currentJobcategory', '')
    }
    extras.loadAIExampleFromLocalStorage()
    if (isAuthenticated.value) getAiUsageCount()
    emitter.on('open-ai-reason-dialog', showModalFromNewAi)
  })

  onBeforeUnmount(() => {
    emitter.off('open-ai-reason-dialog', showModalFromNewAi)
  })

  watch(isAuthenticated, (val) => {
    if (val) extras.authStateChanged()
  })

  watch(
    () => formatData.ai_company_url,
    () => {
      isCompanyInfoExtracted.value = false
    }
  )

  return {
    globalVar: globalVar as typeof globalVar & {
      in_app_payments?: boolean
    },
    isAuthenticated,
    aiMyprVisible,
    closeAiMyprDialog,
    openLoginDialog,
    aiResultView,
    aiResultMyprResult,
    fullscreenLoading,
    loadingText,
    aiGeneratedExampleReason,
    hopeIndustryOptions,
    hopeCareerJobcategoryOptions,
    isSendMail,
    aiUsageCount,
    aiMaxUsageCount,
    isExtractingCompanyInfo,
    isCompanyInfoExtracted,
    exampleDialogVisible,
    tipsDialogVisible,
    summarizeModalVisible,
    summarizePreview,
    aiIsStreaming,
    aiIsThinking,
    aiThinkingLabel,
    aiReasoningText,
    aiShowReasoning,
    toggleAiReasoning,
    reason,
    getLicences,
    aiMyprCtaState,
    summarizeTargetLength,
    summarizePreviewFirst,
    summarizePreviewDisplayText,
    reasonClear,
    updateReason,
    showModalFromNewAi,
    toggleAIMyprDialog: () => {
      aiResultView.value = false
    },
    extractCompanyInfo: generate.extractCompanyInfo,
    handleAIMypr: generate.handleAIMypr,
    goYagiprimeRegister: generate.goYagiprimeRegister,
    applyAiMyprResult: generate.applyAiMyprResult,
    openSummarizeModal: extras.openSummarizeModal,
    applySummarize: extras.applySummarize,
    loadSummarizePreview: extras.loadSummarizePreview,
    closeSummarizePreview: extras.closeSummarizePreview,
    openYagiprimeFromPreview: extras.openYagiprimeFromPreview,
    applySummarizePreview: extras.applySummarizePreview,
    showYagiprimeForSummarize: extras.showYagiprimeForSummarize,
    insertSentence: extras.insertSentence,
    updateFormatField
  }
}
